import {
    Box,
    Button,
    Card,
    Divider,
    IconButton,
    Typography,
} from "@mui/material";
import AuthentigateFooter from "../../AuthentigateFooter";
import Link from "next/link";
import { observer } from "mobx-react-lite";
import MinusIcon from "@mui/icons-material/Remove";
import PlusIcon from "@mui/icons-material/Add";
import { useCartStore } from "../../../stores/CartStore";
import { Toaster } from "react-hot-toast";
import React from "react";
import { priceFormatter } from "../../../utils/helpers";
import CartExpiryBanner from "../../cart/CartExpiryBanner";
import { EcommerceEvent } from "../../../models/Event";

interface LineBusterProps {
    eventList: EcommerceEvent;
    errorMessage: string | null;
    allowedDepartmentIds: number[];
    basePath?: string;
}

const getDepartmentServiceFeeTotal = (department: any): number =>
    (department?.serviceFees ?? []).reduce(
        (sum: number, fee: { value: number }) => sum + fee.value,
        0,
    );

const departmentHasTax = (department: any, eventList: any): boolean =>
    !eventList?.event?.taxDisabled &&
    !department?.taxInclusive &&
    (department?.taxes ?? []).some((tax: { amount: number }) => tax.amount > 0);

const LineBuster = observer(
    ({
        eventList,
        errorMessage,
        allowedDepartmentIds,
        basePath = "/m/cne",
    }: LineBusterProps) => {
        const cartStore = useCartStore("cne");
        const [ticketQuantities, setTicketQuantities] = React.useState<{
            [key: number]: number;
        }>({});

        React.useEffect(() => {
            if (cartStore) {
                const quantities: { [key: number]: number } = {};
                allowedDepartmentIds.forEach((departmentId) => {
                    quantities[departmentId] = cartStore.getDepartmentQuantity(
                        departmentId,
                        false,
                    );
                });
                setTicketQuantities(quantities);
            }
        }, [cartStore?.cart, allowedDepartmentIds]);

        if (!cartStore) return null;

        // Filter departments from eventList based on allowedDepartmentIds
        const availableDepartments =
            eventList?.event?.departments?.filter((dept: any) =>
                allowedDepartmentIds.includes(dept.id),
            ) || [];

        // Calculate totals from actual cart data
        const subtotal = cartStore.ticketBaseSubtotal;
        const totalFees =
            cartStore.cart?.items.reduce((acc, item) => {
                if (
                    allowedDepartmentIds.includes(item.department.id) &&
                    !item.comp
                ) {
                    return (
                        acc +
                        item.serviceFees.reduce(
                            (sum, fee) => sum + fee.amount,
                            0,
                        )
                    );
                }
                return acc;
            }, 0) || 0;
        const taxes = cartStore.totalTaxes;
        const total = cartStore.total;

        const totalTicketQuantity = Object.values(ticketQuantities).reduce(
            (sum, qty) => sum + qty,
            0,
        );

        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100vh",
                    background:
                        "linear-gradient(180deg, #FFFFFF 0%, #FFF5F7 100%)",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        background:
                            "linear-gradient(135deg, #9114A0 0%, #F4145E 100%)",
                        color: "white",
                        py: 4,
                        textAlign: "center",
                        boxShadow: "0 2px 10px rgba(145, 20, 160, 0.3)",
                        mb: 3,
                    }}
                >
                    <img
                        src="https://cdn.authentigate.ca/clients/70/CNE%20Spotlight%20Logo.png"
                        alt="CNE 2025"
                        style={{
                            width: "120px",
                            height: "auto",
                            marginBottom: "16px",
                        }}
                    />
                    <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
                        2026 CNE
                    </Typography>
                    {/* <Typography variant="body1">
                        Select your tickets below
                    </Typography> */}
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        p: 2,
                        maxWidth: "500px",
                        margin: "0 auto",
                        width: "100%",
                        flex: 1,
                    }}
                >
                    {availableDepartments.length === 0 ? (
                        <Card
                            sx={{
                                width: "100%",
                                borderRadius: "16px",
                                background: "#FFFFFF",
                                p: 4,
                                textAlign: "center",
                                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                                border: "1px solid rgba(145, 20, 160, 0.1)",
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#333",
                                    mb: 1,
                                }}
                            >
                                No Tickets Available
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: "#666",
                                }}
                            >
                                Sorry, there are no tickets available at this
                                time. Please check back later.
                            </Typography>
                        </Card>
                    ) : (
                        availableDepartments.map((department) => (
                            <Card
                                key={department.id}
                                sx={{
                                    width: "100%",
                                    borderRadius: "16px",
                                    border: "2px solid #9114A0",
                                    background:
                                        "linear-gradient(135deg, rgba(145, 20, 160, 0.08) 0%, rgba(244, 20, 94, 0.08) 100%)",
                                    p: 2,
                                    mb: 2,
                                    boxShadow:
                                        "0 4px 20px rgba(145, 20, 160, 0.15)",
                                    transition: "all 0.3s ease",
                                    "&:hover": {
                                        transform: "translateY(-2px)",
                                        boxShadow:
                                            "0 6px 25px rgba(145, 20, 160, 0.2)",
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        mb: 1,
                                    }}
                                >
                                    <Box>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: "bold",
                                                color: "#333",
                                            }}
                                        >
                                            {department.name}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: "#666", mt: 0.5 }}
                                        >
                                            {priceFormatter(department.price)}
                                        </Typography>
                                        {getDepartmentServiceFeeTotal(
                                            department,
                                        ) > 0 && (
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: "#888",
                                                    display: "block",
                                                }}
                                            >
                                                +{" "}
                                                {priceFormatter(
                                                    getDepartmentServiceFeeTotal(
                                                        department,
                                                    ),
                                                )}{" "}
                                                service fee
                                            </Typography>
                                        )}
                                        {department.metadata?.lineBuster
                                            ?.description && (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "#888" }}
                                            >
                                                {
                                                    department.metadata
                                                        .lineBuster.description
                                                }
                                            </Typography>
                                        )}
                                    </Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            background: "#FFFFFF",
                                            borderRadius: "50px",
                                            p: 0.5,
                                            border: "2px solid #9114A0",
                                            boxShadow:
                                                "0 2px 8px rgba(145, 20, 160, 0.1)",
                                        }}
                                    >
                                        <IconButton
                                            sx={{
                                                backgroundColor:
                                                    (ticketQuantities[
                                                        department.id
                                                    ] || 0) === 0
                                                        ? "#F5F5F5"
                                                        : "#F4145E",
                                                color: "white",
                                                "&:hover": {
                                                    backgroundColor:
                                                        (ticketQuantities[
                                                            department.id
                                                        ] || 0) === 0
                                                            ? "#F5F5F5"
                                                            : "#E01050",
                                                    transform: "scale(1.05)",
                                                },
                                                "&:disabled": {
                                                    backgroundColor: "#F5F5F5",
                                                    color: "#CCC",
                                                },
                                                width: "40px",
                                                height: "40px",
                                                transition: "all 0.2s ease",
                                            }}
                                            disabled={
                                                (ticketQuantities[
                                                    department.id
                                                ] || 0) === 0
                                            }
                                            onClick={() => {
                                                cartStore.decrementGaQuantity(
                                                    department.id,
                                                );
                                            }}
                                        >
                                            <MinusIcon />
                                        </IconButton>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                minWidth: "40px",
                                                textAlign: "center",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {ticketQuantities[department.id] ||
                                                0}
                                        </Typography>
                                        <IconButton
                                            sx={{
                                                backgroundColor: "#9114A0",
                                                color: "white",
                                                "&:hover": {
                                                    backgroundColor: "#7E1290",
                                                    transform: "scale(1.05)",
                                                },
                                                width: "40px",
                                                height: "40px",
                                                transition: "all 0.2s ease",
                                            }}
                                            onClick={() => {
                                                cartStore.incrementGaQuantity(
                                                    department.id,
                                                );
                                            }}
                                        >
                                            <PlusIcon />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Card>
                        ))
                    )}

                    {totalTicketQuantity > 0 && (
                        <Card
                            sx={{
                                width: "100%",
                                borderRadius: "16px",
                                background: "#FFFFFF",
                                p: 3,
                                boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
                                border: "1px solid rgba(145, 20, 160, 0.1)",
                                animation: "slideIn 0.3s ease-out",
                                "@keyframes slideIn": {
                                    from: {
                                        opacity: 0,
                                        transform: "translateY(-10px)",
                                    },
                                    to: {
                                        opacity: 1,
                                        transform: "translateY(0)",
                                    },
                                },
                            }}
                        >
                            {cartStore.cartExpiresInSeconds && (
                                <Box sx={{ width: "100%", mb: 2 }}>
                                    <CartExpiryBanner
                                        expiresInSeconds={
                                            cartStore.cartExpiresInSeconds
                                        }
                                        onTimeUp={() => {
                                            cartStore.clearCart();
                                        }}
                                    />
                                </Box>
                            )}
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    mb: 1,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{ color: "#666" }}
                                >
                                    {totalTicketQuantity} Ticket
                                    {totalTicketQuantity > 1 ? "s" : ""}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "#333" }}
                                >
                                    {priceFormatter(subtotal)}
                                </Typography>
                            </Box>
                            {totalFees > 0 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "#666" }}
                                    >
                                        Service Fee
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "#333" }}
                                    >
                                        {priceFormatter(totalFees)}
                                    </Typography>
                                </Box>
                            )}
                            {taxes > 0 && (
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        mb: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "#666" }}
                                    >
                                        Tax
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: "#333" }}
                                    >
                                        {priceFormatter(taxes)}
                                    </Typography>
                                </Box>
                            )}
                            <Divider sx={{ my: 1.5 }} />
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                }}
                            >
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: "bold", color: "#333" }}
                                >
                                    Total
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        fontWeight: "bold",
                                        color: "#9114A0",
                                    }}
                                >
                                    {priceFormatter(total)}
                                </Typography>
                            </Box>
                        </Card>
                    )}

                    {cartStore.hasItems && availableDepartments.length > 0 && (
                        <Box
                            sx={{
                                position: "sticky",
                                bottom: 0,
                                zIndex: 10,
                                mt: "auto",
                                mx: -2,
                                px: 2,
                                pt: 2,
                                pb: "calc(12px + env(safe-area-inset-bottom))",
                                background:
                                    "linear-gradient(180deg, rgba(255, 245, 247, 0) 0%, #FFF5F7 35%)",
                            }}
                        >
                            <Link
                                href={`${basePath}/checkout`}
                                style={{
                                    width: "100%",
                                    textDecoration: "none",
                                }}
                            >
                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{
                                        py: 1.5,
                                        borderRadius: "12px",
                                        fontSize: "1rem",
                                        fontWeight: "bold",
                                        background:
                                            "linear-gradient(135deg, #9114A0 0%, #F4145E 100%)",
                                        boxShadow:
                                            "0 4px 20px rgba(145, 20, 160, 0.4)",
                                        "&:hover": {
                                            background:
                                                "linear-gradient(135deg, #7E1290 0%, #E01050 100%)",
                                            transform: "translateY(-2px)",
                                            boxShadow:
                                                "0 6px 25px rgba(145, 20, 160, 0.5)",
                                        },
                                        transition: "all 0.3s ease",
                                    }}
                                >
                                    {`Checkout • ${totalTicketQuantity} ticket${
                                        totalTicketQuantity > 1 ? "s" : ""
                                    }`}
                                </Button>
                            </Link>
                        </Box>
                    )}
                </Box>
                {/* Footer decoration - matching thank you page */}
                <Box sx={{ position: "relative", mt: 4 }}>
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 1440 200"
                        style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "100px",
                            display: "block",
                        }}
                    >
                        <circle
                            cx="200"
                            cy="400"
                            r="400"
                            fill="#9114A0"
                            fillOpacity={0.1}
                        ></circle>
                        <circle
                            cx="1200"
                            cy="500"
                            r="500"
                            fill="#F4145E"
                            fillOpacity={0.1}
                        ></circle>
                    </svg>
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: "20px",
                            width: "100%",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <Link href="https://authentigate.ca" target="_blank">
                            <img
                                src="https://tickets.authentigate.ca/uploads/28/ag-black-transparent.png"
                                alt="Powered by AuthentiGATE"
                                style={{
                                    display: "block",
                                    width: "175px",
                                    maxWidth: "90%",
                                    opacity: 0.7,
                                }}
                            />
                        </Link>
                    </Box>
                </Box>
                <Toaster />
            </Box>
        );
    },
);

export default LineBuster;
